import { Box, Button, Container, Divider, Title } from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { useState } from 'react';
import { Check, X } from 'tabler-icons-react';
import { Countries } from '../../../../enums/countries.enum';
import { OpenComponent } from '../../../../enums/misc.enum';
import { Roles } from '../../../../enums/roles-enum';
import { removeEmptyPropertiesWithNested } from '../../../../helpers/filters.helpers';
import useAuth from '../../../../hooks/useAuth';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../../../hooks/useDidMountEffect';
import {
  BranchUserDetailsForm,
  CreateBranchDetailsForm,
  CreateMerchantBranchesRequest,
} from '../../../../type/entity.types';
import { ErrorResponse } from '../../../../type/error-response.types';
import BackButton from '../../../buttons/back-button.component';
import {
  AdminCreateBranchSchema,
  BranchUserDetailsSchema,
  CreateBranchSchema,
} from '../schemas/create-branch.schema';
import BranchDetails from './branch-details.component';
import BranchUserDetails from './branch-user-details.component';

function CreateBranchEntity(props: Props) {
  const { setComponentManager, setRefreshTable } = props;
  const [isLoading, setIsLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const form = useForm<CreateBranchDetailsForm>({
    initialValues: {
      partnerId: '',
      resellerId: '',
      merchantId: '',
      entityName: '',
      buildingNameAndNumber: '',
      line1: '',
      city: '',
      state: '',
      postalCode: '',
      barangay: '',
      countryId: Countries.PHILIPPINES,
      fax: '',
      mobile: '',
      primary: '',
    },
    // {
    //   partnerId: '',
    //   resellerId: '',
    //   merchantId: '',

    //   entityName: '',
    //   buildingNameAndNumber: '',
    //   line1: '',
    //   city: '',
    //   state: '',
    //   postalCode: '',
    //   barangay: '',
    //   countryId: Countries.PHILIPPINES,
    //   fax: '',
    //   mobile: '',
    //   primary: '',
    // },
    schema: joiResolver(
      auth?.role === Roles.ADMINISTRATOR
        ? AdminCreateBranchSchema
        : CreateBranchSchema
    ),
  });

  const userDetailsForm = useForm<BranchUserDetailsForm>({
    initialValues: {
      firstName: '',
      middleName: '',
      noMiddleName: false,
      lastName: '',
      username: '',
      password: '',
      confirmPassword: '',
      userEmail: '',
      copyBranchAddress: false,
      buildingNameAndNumber: '',
      line1: '',
      city: '',
      state: '',
      postalCode: '',
      barangay: '',
      countryId: Countries.PHILIPPINES,
      fax: '',
      mobile: '',
      primary: '',
      pin: '',
    },
    // {
    //   firstName: '',
    //   middleName: '',
    //   lastName: '',
    //   username: '',
    //   password: '',
    //   confirmPassword: '',
    //   userEmail: '',
    //   buildingNameAndNumber: '',
    //   line1: '',
    //   city: '',
    //   state: '',
    //   postalCode: '',
    //   barangay: '',
    //   countryId: Countries.PHILIPPINES,
    //   fax: '',
    //   mobile: '',
    //   primary: '',
    //   pin: '',
    // },
    schema: joiResolver(BranchUserDetailsSchema),
  });

  useDidMountEffect(() => {}, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const formHasErrors = form.validate().hasErrors;
    const userFormHasErrors = userDetailsForm.validate().hasErrors;

    if (formHasErrors || userFormHasErrors) {
      return;
    }

    const formValues = form.values;
    const userValues = userDetailsForm.values;

    const entityAddress = {
      city: formValues.city,
      line1: formValues.line1,
      buildingNameAndNumber: formValues.buildingNameAndNumber,
      state: formValues.state,
      postalCode: formValues.postalCode,
      barangay: formValues.barangay,
      countryId: formValues.countryId,
      fax: formValues.fax,
    };

    const userAddress = {
      city: userValues.city,
      line1: userValues.line1,
      buildingNameAndNumber: userValues.buildingNameAndNumber,
      state: userValues.state,
      postalCode: userValues.postalCode,
      barangay: userValues.barangay,
      countryId: userValues.countryId,
      fax: userValues.fax,
    };

    const request: CreateMerchantBranchesRequest = {
      branchName: formValues.entityName,
      partnerId: formValues.partnerId!,
      resellerId: formValues.resellerId!,
      merchantId: formValues.merchantId!,

      contact: {
        phone: {
          primary: formValues.primary,
          mobile: formValues.mobile,
        },
      },

      address: entityAddress,

      user: {
        firstName: userValues.firstName,
        middleName: userValues.middleName,
        lastName: userValues.lastName,
        username: userValues.username,
        password: userValues.password,
        pin: userValues.pin,

        contact: {
          email: userValues.userEmail,
          phone: {
            primary: userValues.primary,
            mobile: userValues.mobile,
          },
        },

        address: userValues.copyBranchAddress ? entityAddress : userAddress,
      },
    };

    const cleanedRequest = removeEmptyPropertiesWithNested(request);

    try {
      setIsLoading(true);

      await axiosPrivate.post('web/merchant_branches', cleanedRequest);

      showNotification({
        id: 'successfully-created-branches',
        message: 'Successfully created merchant branch',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
      setRefreshTable((prev) => !prev);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-create-branches',
        message: error?.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Container fluid>
        <BackButton
          handleClick={() => {
            setComponentManager(OpenComponent.HOME);
          }}
        />
        <Title order={2} my='lg'>
          Merchant Branch Registration
        </Title>
        <Divider my='xl' />
        <form onSubmit={handleSubmit}>
          <BranchDetails
            form={form}
            isAdmin={auth?.role === Roles.ADMINISTRATOR}
          />
          <Divider my='xl' />
          <BranchUserDetails form={userDetailsForm} />
          <Box
            my='md'
            mt={30}
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button type='submit' loading={isLoading}>
              Submit
            </Button>
          </Box>
        </form>
      </Container>
    </>
  );
}

export default CreateBranchEntity;

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}
